import React from 'react';
import { BootResponse, LoginResponse } from 'constants/apiResponses.types';
import {
  EBroadcastChannel,
  EEventsType,
  EFeatureFlag,
  ELocalStorageKeys,
  ELoginEvents,
  ELoginEventsV2,
  ELoginMethods,
  ELoginMode,
  ELoginResultReason,
  EPlatformType,
  EPublisherWebhookEventTypeV2,
  EQueryParams,
  EResultOptions,
  ESessionStorageKeys,
  EStorageType,
  EStorePhase,
} from 'constants/enums';
import { broadcastRegistry } from 'lib/BroadcastChannelManager';
import { clearStoreRefreshSession } from 'hooks/useStoreRefresh';
import useFacebook from 'hooks/useFacebook';
import { getPlatformData, getPopupSearchParams } from 'utils';
import { webStorageUtil } from 'state/webStorage.state.service';
import useGoogle from 'hooks/useGoogle';
import useUrlQuery from 'hooks/useUrlQuery';
import useCustomEvents from 'hooks/useCustomEvents';
import useLoginEvents from 'hooks/useLoginEvents';
import { useNavigate } from 'react-router-dom';
import { useObservable } from 'state/observable/observable.state.hook';
import { LoginData, LoginRequestData } from 'pages/login/types';
import useApi from 'hooks/useApi';
import { browserName, isAndroid, isIOS, isMobile, osName } from 'react-device-detect';
import { loginStateService } from 'state/login.state.service';
import { useTranslation } from 'react-i18next';
import { useAppState } from './app.state.hook';
import { loginPageService } from 'pages/login/login-page.service';
import useIndexedDB from 'hooks/useIndexedDB';
import { v4 as uuidv4 } from 'uuid';
import { CoralogixRum } from '@coralogix/browser';
import useCoralogixMonitoring from '../../hooks/useCoralogixMonitoring';
import { clearProgressBarState } from '@appcharge/shared-ui';

export const DEFAULT_TTL = 3600000;

export const useLoginState = () => {
  const API = useApi({
    platform: isMobile ? osName : browserName,
  });
  const publisherMetaData = API.getPublisherMeta.data as BootResponse;
  const facebook = useFacebook(publisherMetaData.integration.playersAuthentication.fbAppId);
  const google = useGoogle();
  const localAddressFromUrl = useUrlQuery(EQueryParams.LOCAL_ADDRESS);
  const customEvents = useCustomEvents();
  const {
    sendPublisherWebhookLoginEvent,
    sendPublisherWebhookLoginEventV2,
    calcLoginDuration,
    calcLoginDurationMs,
    recordLoginStartTime,
  } = useLoginEvents();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { setDisplayLoader } = useAppState();
  const { addValue, deleteAll } = useIndexedDB(['sessions']);
  const { sendHealthLog } = useCoralogixMonitoring();

  // Handle local address from URL - save to local storage if present
  React.useEffect(() => {
    if (localAddressFromUrl) {
      webStorageUtil.set(ELocalStorageKeys.LOCAL_ADDRESS, localAddressFromUrl);
    }
  }, [localAddressFromUrl]);

  // Get local address from storage or URL
  const localAddress = localAddressFromUrl || webStorageUtil.get(ELocalStorageKeys.LOCAL_ADDRESS);

  const storeSessionTokenInDB = async (sessionToken: string, id: string) => {
    try {
      await addValue.mutateAsync({
        tableName: 'sessions',
        value: {
          id: id,
          sessionToken: sessionToken,
        },
      });
    } catch (error) {
      console.error('Error storing session token in DB', error);
    }
  };

  const failedLoginHandler = async ({
    method,
    msg,
    enrichedCustomMsg,
    isTempSession = false,
    shouldBlockStoreInCampaign = false,
    title,
  }: {
    method: string;
    msg?: string;
    enrichedCustomMsg?: string;
    isTempSession?: boolean;
    shouldBlockStoreInCampaign?: boolean;
    title?: string;
  }) => {
    webStorageUtil.remove(ELocalStorageKeys.LOCAL_ADDRESS);

    const loginFailureData = {
      result: EResultOptions.FAILED,
      reason: msg || enrichedCustomMsg || ELoginResultReason.UNKNOWN,
      duration: calcLoginDurationMs(),
    };
    customEvents.sendCustomEvent(
      EEventsType.LOGIN_RESOLVED,
      {
        ...loginPageService.getLoginEventsData({
          eventSharedProps: customEvents.getEventsSharedProps(),
          method: method as ELoginMethods,
        }),
        authMethod: method,
        ...loginFailureData,
        playerId: null,
        login_duration: calcLoginDuration(),
        platform: getPlatformData(),
        isTempSession,
      },
      EStorePhase.PRE_LOGIN
    );
    await sendPublisherWebhookLoginEvent({
      eventName: ELoginEvents.LOGIN_RESOLVED,
      method: method as ELoginMethods,
      options: loginFailureData,
    });
    await sendPublisherWebhookLoginEventV2({
      eventName: ELoginEventsV2.WEBSTORE_LOGIN_COMPLETED,
      eventType: EPublisherWebhookEventTypeV2.PRE_LOGIN,
      method: method as ELoginMethods,
      options: loginFailureData,
    });
    const queryParams = new URLSearchParams({
      error: 'auth',
      ...(msg && { msg }),
      ...(title && { title }),
    });
    navigate(`/failed?${queryParams.toString()}`, {
      state: {
        ...(enrichedCustomMsg && { enrichedCustomMsg }),
        ...(shouldBlockStoreInCampaign && { shouldBlockStore: true }),
        ...(title && { title }),
      },
    });
  };

  const handleLoginError = async (error: any, authMethod: string) => {
    const defaultErrorMessage =
      publisherMetaData.storeTheme.login.errorMessage?.subTitle ||
      t('login.errorMessages.loginFailed');
    const { enrichedMessage, code, message, title } = error.response?.data || {};
    const errorCode = error.response?.data?.code;
    const errorMessage = enrichedMessage
      ? undefined
      : code === 100
        ? message
        : errorCode >= 500 && errorCode <= 3000
          ? t('login.errorMessages.serviceIsDown')
          : defaultErrorMessage;
    await failedLoginHandler({
      method: authMethod,
      msg: errorMessage,
      enrichedCustomMsg: enrichedMessage,
      title,
    });
  };

  const loginResponse = async (response: LoginData) => {
    setDisplayLoader(true);
    try {
      const loginPayload = prepareLoginPayload(response);
      const { data } = await performLogin(loginPayload);
      await processSuccessfulLogin(response, data);
    } catch (error: any) {
      await handleLoginError(error, response.authMethod);
    }
  };

  const prepareLoginPayload = (response: LoginData) => {
    const isProfilesEnabled = publisherMetaData.integration.isProfilesEnabled;
    const hasIntegrationFeatureFlag =
      publisherMetaData?.featureFlags[EFeatureFlag.INTEGRATION_PROFILES];
    const profileName = webStorageUtil.get(ELocalStorageKeys.INTEGRATION_PORFILE);

    // Handle profile image URL for social logins
    if (response.authMethod === ELoginMethods.FACEBOOK) {
      try {
        facebook
          .getFacebookProfile(response.userToken!)
          .then((url) => {
            response.profileImageUrl = url;
          })
          .catch((error) => {
            console.error('Failed to load profile picture', error);
          });
      } catch (error) {
        console.error('Failed to load profile picture', error);
      }
    }
    if (response.authMethod === ELoginMethods.GOOGLE) {
      google
        .getGoogleProfile(response.userToken!)
        .then((url) => {
          response.profileImageUrl = url;
        })
        .catch((error) => {
          console.error('Failed to load Google profile picture', error);
        });
    }

    // Add profile to user token if needed
    if (isProfilesEnabled && hasIntegrationFeatureFlag && profileName) {
      response.userToken = `${response.userToken}::${profileName}`;
    }

    const sessionData = webStorageUtil.getStorageWithFallback();
    const loginData: {
      data: LoginRequestData;
    } = {
      data: {
        publisherId: publisherMetaData.storeTheme.publisherId,
        localAddress: localAddress,
        sessionId: sessionData.id,
        ...response,
      },
    };

    // Add OS information if feature flag is enabled
    if (publisherMetaData?.featureFlags.store_send_os_authlogin) {
      loginData.data.os = isIOS
        ? EPlatformType.IOS
        : isAndroid
          ? EPlatformType.ANDROID
          : EPlatformType.WEB;
    }

    if (response.authMethod === ELoginMethods.OTP && loginStateService.proofKey.get()) {
      loginStateService.setLoggingWithProofKey(true);
    }

    // Send login attempt event
    customEvents.sendCustomEvent(
      EEventsType.LOGIN_ATTEMPTED,
      loginPageService.getLoginEventsData({
        eventSharedProps: customEvents.getEventsSharedProps(),
        method: response.authMethod,
      }),
      EStorePhase.PRE_LOGIN
    );

    // Prepare platform information
    const platformBase = getPlatformData().toUpperCase();
    const platform =
      isProfilesEnabled && hasIntegrationFeatureFlag && profileName
        ? `${platformBase}::${profileName}`
        : platformBase;

    return {
      ...loginData.data,
      platform,
      isIntegrationProfilesEnabled: isProfilesEnabled,
    };
  };

  const performLogin = async (loginPayload: any): Promise<any> => {
    return await API.login.mutateAsync(loginPayload);
  };

  const processSuccessfulLogin = async (
    response: LoginData,
    data: LoginResponse,
    isTempSession = false
  ) => {
    const loginDuration = calcLoginDuration();
    const loginDataToSend = {
      result: EResultOptions.SUCCESS,
      playerId: data.playerId,
    };

    // Handle error code 100 as a special case
    if (data.errorCode === 100) {
      return failedLoginHandler({
        method: response.authMethod,
        msg: data.errorMessage,
      });
    }

    // Store session token in DB if available
    if (data.sessionToken) {
      await storeSessionTokenInDB(data.sessionToken, data.playerId);
    }

    data.sessionToken && webStorageUtil.set(ELocalStorageKeys.SESSION_TOKEN, data.sessionToken);

    const previousPlayer = webStorageUtil.get(ELocalStorageKeys.PLAYER_DATA);
    const isDifferentPlayer =
      !previousPlayer?.playerId || previousPlayer.playerId !== data.playerId;
    if (isDifferentPlayer) {
      broadcastRegistry.resetCounter(EBroadcastChannel.STORE_REFRESH_CHANNEL_NAME);
      clearStoreRefreshSession();
    }

    // Save player data
    webStorageUtil.set(ELocalStorageKeys.PLAYER_DATA, {
      playerId: data.playerId,
      playerName: data.playerName,
      playerCountry: data.playerCountry,
      profileImageUrl: data.profileImageUrl || data.playerProfileImage,
      authMethod: response.authMethod,
    });

    // Update CoralogixRum user context with player data
    CoralogixRum.setUserContext({
      user_id: data.playerId,
      user_name: data.playerName,
      user_email: '',
      user_metadata: {
        playerId: data.playerId,
        playerName: data.playerName,
        playerCountry: data.playerCountry,
        authMethod: response.authMethod,
        isTempSession,
      },
    });

    // Update CoralogixRum labels with player info
    CoralogixRum.setLabels({
      playerId: data.playerId,
      playerName: data.playerName,
      playerCountry: data.playerCountry,
      authMethod: response.authMethod,
      isTempSession: isTempSession.toString(),
    });

    // Log successful login health
    sendHealthLog('periodic');

    webStorageUtil.remove(ELocalStorageKeys.LOCAL_ADDRESS);

    customEvents.setDistinctId();

    const loginDurationMs = calcLoginDurationMs();

    // Send login result event
    customEvents.sendCustomEvent(
      EEventsType.LOGIN_RESOLVED,
      {
        ...loginPageService.getLoginEventsData({
          eventSharedProps: customEvents.getEventsSharedProps(),
          method: response.authMethod,
        }),
        authMethod: response.authMethod,
        ...loginDataToSend,
        country: data.playerCountry || null,
        login_duration: loginDuration,
        duration: loginDurationMs,
        platform: getPlatformData(),
        isTempSession,
      },
      EStorePhase.POST_LOGIN
    );

    // Send webhook event
    await sendPublisherWebhookLoginEvent({
      eventName: ELoginEvents.LOGIN_RESOLVED,
      method: response.authMethod,
      options: {
        ...loginDataToSend,
        duration: loginDurationMs,
      },
    });

    await sendPublisherWebhookLoginEventV2({
      eventName: ELoginEventsV2.WEBSTORE_LOGIN_COMPLETED,
      eventType: EPublisherWebhookEventTypeV2.POST_LOGIN,
      method: response.authMethod,
      options: {
        ...loginDataToSend,
        duration: loginDurationMs,
      },
    });

    const getPreservedUrlParams = () =>
      response.authMethod === ELoginMethods.PORTAL_REDIRECT
        ? getPopupSearchParams(window.location.search)
        : '';

    if (response.authMethod === ELoginMethods.OTP && response.otp?.accessToken) {
      API.getCampaignToken.mutate(
        { accessToken: response.otp.accessToken },
        {
          onSuccess: (data) => {
            if (data.d10xPayload) {
              sessionStorage.setItem('dynamoPayload', data.d10xPayload);
            }
            const newRoute = `/shop${data.campaignToken ? `?${EQueryParams.FROM}=campaign&${EQueryParams.TOKEN}=${data.campaignToken}` : ''}`;

            navigate(newRoute);
          },
          onError: (error) => {
            console.error('Error getting campaign token', error);
            navigate(`/shop`);
          },
        }
      );
    } else if (response.authMethod !== ELoginMethods.DEEP_LINK) {
      const preservedParams = getPreservedUrlParams();
      navigate(`/shop${preservedParams}`);
    }
  };

  const navigateToMode = (mode: ELoginMode) => {
    return mode !== 'providers' ? navigate(`./${mode}`) : navigate(-1);
  };

  const loginButtonClickWrapper = async (callback: Function, method: ELoginMethods) => {
    if (
      method === ELoginMethods.FACEBOOK ||
      method === ELoginMethods.GOOGLE ||
      method === ELoginMethods.APPLE
    ) {
      recordLoginStartTime();
      setDisplayLoader(true);
    }
    if (!loginPageService.isWebviewBrowser()) {
      customEvents.sendCustomEvent(
        EEventsType.LOGIN_METHOD_SELECTED,
        loginPageService.getLoginEventsData({
          eventSharedProps: customEvents.getEventsSharedProps(),
          method,
        }),
        EStorePhase.PRE_LOGIN
      );
    }
    sendPublisherWebhookLoginEvent({ eventName: ELoginEvents.LOGIN_METHOD_SELECTED, method });
    sendPublisherWebhookLoginEventV2({
      eventName: ELoginEventsV2.WEBSTORE_LOGIN_METHOD_SELECTED,
      eventType: EPublisherWebhookEventTypeV2.PRE_LOGIN,
      method,
    });
    webStorageUtil.set(ESessionStorageKeys.REDIRECT_STATE, `${method}redirect`);
    callback();
  };

  const handleBackBtnClicked = async () => {
    const mode = loginStateService.mode.get() as ELoginMode;
    const method = loginPageService.mapLoginModeToLoginMethod[mode];
    navigateToMode(ELoginMode.PROVIDERS);
    customEvents.sendCustomEvent(
      EEventsType.LOGIN_CANCELED,
      loginPageService.getLoginEventsData({
        eventSharedProps: customEvents.getEventsSharedProps(),
        method,
      }),
      EStorePhase.PRE_LOGIN
    );
    sendPublisherWebhookLoginEvent({
      eventName: ELoginEvents.LOGIN_CANCELED,
      method,
    });
    sendPublisherWebhookLoginEventV2({
      eventName: ELoginEventsV2.WEBSTORE_LOGIN_CANCELED,
      eventType: EPublisherWebhookEventTypeV2.PRE_LOGIN,
      method,
    });
  };

  const resetAppState = (utmSource?: string) => {
    API.getPostLoginData.remove();
    API.getOffers.remove();
    API.keepAlive.reset();

    webStorageUtil.clear();
    clearProgressBarState();

    broadcastRegistry.resetCounter(EBroadcastChannel.STORE_REFRESH_CHANNEL_NAME);
    clearStoreRefreshSession();

    const newSession = {
      id: uuidv4(),
      ttl: Date.now() + DEFAULT_TTL,
    };
    webStorageUtil.set(ESessionStorageKeys.SESSION_DATA, newSession, EStorageType.SESSION_STORAGE);
    webStorageUtil.set(ELocalStorageKeys.SESSION_DATA, newSession, EStorageType.LOCAL_STORAGE);
    webStorageUtil.remove(ESessionStorageKeys.REDIRECT_STATE);
    // Delete local address from local storage on app reset
    webStorageUtil.remove(ELocalStorageKeys.LOCAL_ADDRESS);
    setDisplayLoader(false);

    navigate(`/login${utmSource ? `?utm_source=${utmSource}` : ''}`, { replace: true });
  };

  const logout = async (utmSource?: string) => {
    // Reset CoralogixRum user context on logout
    CoralogixRum.setUserContext({
      user_id: '',
      user_name: '',
      user_email: '',
      user_metadata: {},
    });

    CoralogixRum.setLabels({
      playerId: '',
      playerName: '',
      playerCountry: '',
      authMethod: '',
      isTempSession: 'false',
    });

    // Log logout health
    sendHealthLog('periodic');

    API.logout.mutate(undefined, {
      onSettled: () => resetAppState(utmSource),
    });
    deleteAll.mutate('sessions');
  };

  return {
    showCookiesOverlay: useObservable(loginStateService.showCookiesOverlay),
    showCookieSettingsDialog: useObservable(loginStateService.showCookieSettingsDialog),
    proofKey: useObservable(loginStateService.proofKey),
    loggingWithProofKey: useObservable(loginStateService.loggingWithProofKey),
    otpToken: useObservable(loginStateService.otpToken),
    mode: useObservable(loginStateService.mode),
    hasConsent: useObservable(loginStateService.hasConsent),
    shouldShowTermsAlert: useObservable(loginStateService.shouldShowTermsAlert),
    isTermsAlertShaking: useObservable(loginStateService.isTermsAlertShaking),
    setShowCookiesOverlay: loginStateService.setShowCookiesOverlay,
    setShowCookieSettingsDialog: loginStateService.setShowCookieSettingsDialog,
    setProofKey: loginStateService.setProofKey,
    setLoggingWithProofKey: loginStateService.setLoggingWithProofKey,
    setOtpToken: loginStateService.setOtpToken,
    setMode: loginStateService.setMode,
    setHasConsent: loginStateService.setHasConsent,
    setShouldShowTermsAlert: loginStateService.setShouldShowTermsAlert,
    setIsTermsAlertShaking: loginStateService.setIsTermsAlertShaking,
    loginResponse,
    processSuccessfulLogin,
    failedLoginHandler,
    navigateToMode,
    loginButtonClickWrapper,
    handleBackBtnClicked,
    logout,
    clear: loginStateService.clear,
  };
};
